<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import { fly, slide } from 'svelte/transition';

	let {
		title = '', // prop or snippet
		//route = '', // disabled for now
		href = null, // link, not dropdown
		button = false, // renders a button
		transparent = false, // has no focus background
		position = 'right', // dropdown menu alignment relative to trigger
		class: className = '',
		children, // contents of the dropdown
		active = true,
	} = $props();

	const handleCloseMenuOnFocused = (e) => {
		// The condition ensures:
		// The current element is focused (currentTarget === activeElement)
		// The click target is exactly the active element (not a child)
		// If both match, it means the user clicked the focused element again, so we blur it to dismiss.
		// If not, do nothing — avoids interfering with clicks inside the menu.

		if (
			document.activeElement != e.currentTarget ||
			e.target != document.activeElement
		) {
			return;
		}

		document.activeElement.blur();
		e.preventDefault();
	};

	//! At the moment, not all pages are stored in the same folders.
	//! This makes path based active classes impractical.
	//! David says 'remove it entirely,' which is what David has done...

	let textColorClass = 'text-indow-grey-dark';
	// let textColorClass = $derived.by(() => {
	// 	return route && page.url.pathname.startsWith(route)
	// 		? 'text-indow-blue'
	// 		: 'text-indow-grey-dark';
	// });

	let innerWidth = $state();

	let getEstimateTransition = (node) => {
		return innerWidth < 768
			? fly(node, { y: '-150%' })
			: slide(node, { axis: 'x' });
	};
</script>

<svelte:window bind:innerWidth />
<div
	class={[
		{ 'h-full place-items-center': button === false },
		{ 'is-button': button === true },
		{ 'is-transparent': transparent === true },
		'menu cursor-pointer content-center transition-all duration-300 ease-in-out',
		textColorClass,
	]}
	role="button"
	tabindex="0"
	onmousedown={handleCloseMenuOnFocused}
>
	{#if children && !href}
		<!-- Dropdown Label -->
		<span
			class={[
				'pointer-events-none px-3 font-medium text-inherit uppercase transition-all duration-200 ease-in-out lg:px-4',
				className,
			]}
		>
			{#if typeof title === 'function'}
				{@render title()}
			{:else}
				{title}
			{/if}
		</span>
	{:else if button}
		<!-- Button -->
		<Button
			{href}
			color="white"
			class={[
				'bg-indow-white text-indow-blue md:!bg-indow-yellow  md:!text-indow-white px-6 py-4 text-center text-base leading-none font-bold tracking-normal no-underline transition-all duration-200 ease-in-out hover:no-underline focus:no-underline max-lg:!px-2 max-md:!py-2 sm:tracking-widest',
				className,
			]}
			transition={getEstimateTransition}
			transitionDuration="300"
			{active}
		>
			{#if typeof title === 'function'}
				{@render title()}
			{:else}
				{title}
			{/if}
		</Button>
	{:else}
		<!-- Link -->
		<a
			class={[
				'block h-full w-full place-content-center px-4 font-medium text-inherit uppercase no-underline transition-all duration-200 ease-in-out md:py-12',
				className,
			]}
			{href}
		>
			{#if typeof title === 'function'}
				{@render title()}
			{:else}
				{title}
			{/if}
		</a>
	{/if}

	<!-- Dropdown Menu -->
	{#if !href}
		<div
			class={[
				{ 'right-0': position === 'left' },
				{ 'right-0 left-0': position === 'full' },
				'dropdown pointer-events-none absolute top-full cursor-default rounded-b bg-white text-inherit opacity-0 shadow-lg transition-all duration-300 ease-in-out',
			]}
		>
			{@render children?.()}
		</div>
	{/if}
</div>

<style lang="postcss">
	/*
	Things Tailwind cannot handle
	*/
	.menu {
		.dropdown:before {
			/* Drop shadow inside menu */
			box-shadow: inset 0px 11px 8px -10px #0004;
			pointer-events: none;
			position: absolute;
			top: 0;
			left: 0;
			height: 100%;
			width: 100%;
			content: '';
		}

		&:focus-within {
			/* Focused/Active Menu */
			&:not(.is-transparent) {
				background-color: var(--color-indow-blue);
			}
			&:not(.is-transparent) > span {
				color: var(--color-indow-white);
				fill: var(--color-indow-white);
			}
			&:is(.is-transparent) > span {
				color: var(--color-indow-blue);
				fill: var(--color-indow-blue);
			}
			/* When the menu item is focused, show the dropdown */
			div {
				pointer-events: all;
				opacity: 1;
			}

			/*
				focus-on and focus-off allow children to
				hide and show when the menu is focused.
				See the mobile menu icon change
			*/
			:global(.focus-on) {
				display: block;
			}
			:global(.focus-off) {
				display: none;
			}
		}

		&:hover:not(:focus):not(.is-button) {
			/* Hovering over not-focued menu — that is also not a button */
			&:not(.is-transparent) {
				background-color: var(--color-gray-50);
			}
			> span,
			> a {
				color: var(--color-indow-blue);
				fill: var(--color-indow-blue);
			}
		}
	}
</style>
